import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './ContentReview.css';

/**
 * DM routing (2026-09-02, Fredrik) — the module-wide default decision
 * maker(s). Lives in the Reviewer Contexts admin tab (moved out of Team
 * Settings same day: this is review-role config, not member management,
 * and the tab shares the setting's DM gating). New cycles auto-assign
 * the default; unassigned cycles route DM notifications to it; with none
 * set, every admin + DM is notified (pre-feature behavior).
 */
interface DefaultDmSettingsProps {
  getAuthHeaders: () => Promise<Record<string, string>>;
}

interface TeamMember {
  _id: string;
  name: string;
  email: string;
  role?: string;
  permissions?: { subcategories?: string[] };
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const isDmCapable = (m: TeamMember) =>
  m.role === 'admin' || m.permissions?.subcategories?.includes('content_review.decision_maker');

const DefaultDmSettings: React.FC<DefaultDmSettingsProps> = ({ getAuthHeaders }) => {
  const [candidates, setCandidates] = useState<TeamMember[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const [settingsRes, teamRes] = await Promise.all([
        axios.get(`${backendUrl}/api/content-review/settings/default-decision-makers`, { headers }),
        axios.get(`${backendUrl}/api/team`, { headers })
      ]);
      setCandidates((teamRes.data || []).filter(isDmCapable));
      setSelectedIds((settingsRes.data?.defaultDecisionMakers || []).map((d: { _id: string }) => d._id));
      setLoaded(true);
    } catch {
      // Read is DM-gated; a 403 (or transient failure) just hides the card.
      setLoaded(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (id: string) => {
    setMsg(null);
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setMsg(null);
    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      const res = await axios.put(
        `${backendUrl}/api/content-review/settings/default-decision-makers`,
        { memberIds: selectedIds },
        { headers }
      );
      setSelectedIds((res.data?.defaultDecisionMakers || []).map((d: { _id: string }) => d._id));
      setMsg('Saved');
    } catch (err: any) {
      setMsg(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 8px' }}>Default Decision Maker(s)</h3>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
        New review cycles are assigned to these decision makers, and their notifications
        (new content, ready-for-decision, reminders) go to them instead of every admin and DM.
        The assignment can be changed per cycle, and it never limits who can act. With none
        selected, every admin and decision maker is notified.
      </p>
      <div className="assigned-dm-picker" style={{ maxWidth: 520 }}>
        {candidates.map(m => (
          <label key={m._id} className="assigned-dm-option">
            <input
              type="checkbox"
              checked={selectedIds.includes(m._id)}
              onChange={() => toggle(m._id)}
            />
            {m.name} <span className="assigned-dm-email">({m.email})</span>
          </label>
        ))}
        <div className="deadline-editor-row">
          <button
            type="button"
            className="review-btn review-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {msg && (
            <span className={`deadline-msg ${msg === 'Saved' ? 'deadline-msg-success' : 'deadline-msg-error'}`}>
              {msg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DefaultDmSettings;
